import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Loader2, Search, ChevronLeft, ChevronRight, AlertTriangle, Receipt,
  TrendingDown, Wallet, Clock, ExternalLink, RefreshCw, ShieldAlert,
  CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import {
  getPlatformPayments, getBillingHealth, getPaymentDetail,
} from "@/services/superAdminService";

const money = (amt, currency = "INR") =>
  amt == null ? "—" : `${currency} ${Number(amt).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * snake_case → readable. Note the /g: `String.replace("_", " ")` swaps only the
 * FIRST underscore, so `trial_to_paid` came out as "trial to_paid".
 */
const humanize = (s) =>
  s == null
    ? "—"
    : String(s)
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
        // Otherwise "upi_mandate_migration" reads as "Upi mandate migration".
        .replace(/\b(upi|gst|api|id)\b/gi, (m) => m.toUpperCase());

/** Matches the labels used on the company-facing subscription page. */
const METHOD_LABELS = {
  upi: "UPI",
  card: "Card",
  emandate: "Card e-Mandate",
  netbanking: "Netbanking",
  wallet: "Wallet",
};
const methodLabel = (m) => (m ? METHOD_LABELS[m] || humanize(m) : null);

const STATUS_TONE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700/40",
  created: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700/40",
  failed: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-700/40",
  refunded: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${
    STATUS_TONE[status] || STATUS_TONE.refunded}`}>
    {status}
  </span>
);

const SEVERITY_TONE = {
  undelivered: "text-rose-600 dark:text-rose-400",
  money_taken_not_applied: "text-rose-600 dark:text-rose-400",
  dead: "text-rose-600 dark:text-rose-400",
  blocking: "text-amber-600 dark:text-amber-400",
  recoverable: "text-amber-600 dark:text-amber-400",
  unconfirmed: "text-slate-500 dark:text-slate-400",
};

const PER_PAGE = 25;

const PlatformPayments = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("payments");

  // ── Ledger ────────────────────────────────────────────────────────────
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [allTime, setAllTime] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // ── Health ────────────────────────────────────────────────────────────
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  // ── Detail drawer ─────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!detailId) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetail(null);
    getPaymentDetail(detailId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.error || "Could not load this payment.");
        setDetailId(null);
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    // Guards against a stale response landing after the user opened a
    // different row — otherwise the drawer shows the wrong payment.
    return () => { cancelled = true; };
  }, [detailId]);

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const res = await getPlatformPayments({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        ...(status !== "all" ? { status } : {}),
        ...(category !== "all" ? { purpose_category: category } : {}),
        ...(allTime ? { all_time: true } : {}),
        ...(search ? { search } : {}),
      });
      setLedger(res);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load payments.");
    } finally {
      setLedgerLoading(false);
    }
  }, [page, status, category, allTime, search]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      setHealth(await getBillingHealth());
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load billing health.");
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => { loadLedger(); }, [loadLedger]);
  // Loaded on mount regardless of the active tab — the badge has to be right
  // before anyone clicks, otherwise it can't do its job.
  useEffect(() => { loadHealth(); }, [loadHealth]);

  /**
   * Filter changes reset to page 1 HERE rather than in an effect.
   *
   * With `useEffect(() => setPage(1), [status, ...])` a filter change fires
   * twice: once with the new filter but the OLD page (fetching a page that may
   * not exist for the narrowed result set), then again after setPage commits.
   * Setting both in the same handler makes it one render and one request.
   */
  const applyFilter = (setter) => (value) => { setter(value); setPage(1); };

  const openCompany = (companyId) => {
    if (!companyId) return;
    // CompanyDetails reads companyId from location.state, not a URL param.
    navigate("/super-admin/companies/details", { state: { companyId } });
  };

  const totalPages = ledger ? Math.max(1, Math.ceil(ledger.total / PER_PAGE)) : 1;
  const needsAttention = health?.totals?.needs_attention ?? 0;
  const inr = ledger?.summary?.by_currency?.find((c) => c.currency === "INR")
    || ledger?.summary?.by_currency?.[0];

  const tiles = [
    { label: "Collected", value: money(inr?.collected, inr?.currency), sub: `${inr?.counts?.paid ?? 0} payments`, icon: Wallet, tone: "emerald" },
    { label: "Failed", value: money(inr?.failed, inr?.currency), sub: `${inr?.counts?.failed ?? 0} attempts`, icon: TrendingDown, tone: "rose" },
    { label: "Awaiting outcome", value: money(inr?.pending, inr?.currency), sub: `${inr?.counts?.created ?? 0} unresolved`, icon: Clock, tone: "amber" },
    { label: "Needs attention", value: String(needsAttention), sub: "billing exceptions", icon: ShieldAlert, tone: needsAttention > 0 ? "rose" : "slate" },
  ];
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
    rose: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300",
    slate: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-foreground">Platform Payments</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Centralized view of all company payments, including failed and pending payment issues.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadLedger(); loadHealth(); }}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Card key={t.label} className="rounded-2xl border border-slate-200/70 dark:border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{t.label}</p>
                    <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-foreground truncate">
                      {ledgerLoading && t.label !== "Needs attention" ? "…" : t.value}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">{t.sub}</p>
                  </div>
                  <div className={`p-2.5 rounded-full shrink-0 ${toneMap[t.tone]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {[
          { id: "payments", label: "Payments" },
          { id: "health", label: "Health", badge: needsAttention },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition flex items-center gap-2 ${
              tab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            {t.label}
            {t.badge > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold h-5 min-w-5 px-1.5">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "payments" ? (
        <>
          {/* Filters */}
          <Card className="rounded-2xl border border-slate-200/70 dark:border-slate-700">
            <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
              <form
                className="relative flex-1"
                onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); setPage(1); }}
              >
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Company name, or a Razorpay order/payment id…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    Clear
                  </button>
                )}
              </form>
              <Select value={status} onValueChange={applyFilter(setStatus)}>
                <SelectTrigger className="w-full sm:w-[150px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {/* Fallback so the dropdown isn't empty on first paint, before
                      the initial fetch returns the server's list. */}
                  {(ledger?.status_options || ["created", "paid", "failed", "refunded"]).map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={applyFilter(setCategory)}>
                <SelectTrigger className="w-full sm:w-[190px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {(ledger?.purpose_options || []).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={allTime ? "default" : "outline"}
                onClick={() => { setAllTime((v) => !v); setPage(1); }}
                className="whitespace-nowrap"
              >
                {allTime ? "All time" : "Last 90 days"}
              </Button>
            </CardContent>
          </Card>

          {/* A capped name-search would otherwise show a partial ledger as if
              it were the whole thing. */}
          {ledger?.search_resolution?.truncated && (
            <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              That search matched more companies than we can filter on — results are partial. Narrow the term.
            </p>
          )}

          <Card className="rounded-2xl border border-slate-200/70 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerLoading ? (
                    <TableRow><TableCell colSpan={6} className="py-12 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                    </TableCell></TableRow>
                  ) : !ledger?.payments?.length ? (
                    <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                      No payments match these filters.
                    </TableCell></TableRow>
                  ) : ledger.payments.map((p) => (
                    <TableRow
                      key={p._id}
                      onClick={() => setDetailId(p._id)}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                    >
                      <TableCell className="whitespace-nowrap text-slate-500 dark:text-slate-400 text-xs">
                        {formatDate(p.created_at)}
                      </TableCell>
                      <TableCell>
                        {p.company ? (
                          <button
                            // Stop propagation so the company link doesn't also
                            // open the payment drawer behind it.
                            onClick={(e) => { e.stopPropagation(); openCompany(p.company._id); }}
                            className="text-primary hover:underline inline-flex items-center gap-1 font-medium"
                          >
                            {p.company.name}
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        ) : (
                          <span className="text-slate-400 italic text-xs">unlinked</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{p.purpose_label}</span>
                        {p.purpose_category === "unknown" && (
                          <span className="block text-[10px] text-slate-400">{p.purpose}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 dark:text-slate-400">
                        {methodLabel(p.payment_method) || "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap">
                        {money(p.amount, p.currency)}
                      </TableCell>
                      <TableCell><StatusPill status={p.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {ledger && ledger.total > PER_PAGE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, ledger.total)} of {ledger.total}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="flex items-center px-3 text-sm text-slate-600 dark:text-slate-300">
                    {page} / {totalPages}
                  </span>
                  <Button variant="outline" size="icon" disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      ) : (
        <HealthTab health={health} loading={healthLoading} onOpenCompany={openCompany} />
      )}

      <PaymentDrawer
        open={!!detailId}
        onOpenChange={(v) => { if (!v) { setDetailId(null); setDetail(null); } }}
        detail={detail}
        loading={detailLoading}
        onOpenCompany={openCompany}
      />
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────
   Payment detail drawer
   ──────────────────────────────────────────────────────────────────────── */

const Field = ({ label, children, mono }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-slate-500 dark:text-slate-400 shrink-0">{label}</span>
    <span className={`text-right text-slate-900 dark:text-foreground break-all ${mono ? "font-mono text-xs" : ""}`}>
      {children ?? "—"}
    </span>
  </div>
);

const Block = ({ title, children }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">
      {title}
    </p>
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 divide-y divide-slate-100 dark:divide-slate-800">
      {children}
    </div>
  </div>
);

const PaymentDrawer = ({ open, onOpenChange, detail, loading, onOpenCompany }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="w-full sm:max-w-lg overflow-y-auto dark:bg-slate-900">
      {loading || !detail ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2">
              {detail.payment.purpose_label}
              <StatusPill status={detail.payment.status} />
            </SheetTitle>
            <SheetDescription>
              {money(detail.payment.amount, detail.payment.currency)}
              {detail.company && <> · {detail.company.name}</>}
              {" · "}{formatDate(detail.payment.createdAt)}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4">
            {/* The question the ledger cannot answer on its own: did this money
                actually produce what the customer paid for? */}
            <div className={`rounded-lg border px-3 py-2.5 ${
              detail.fulfilment.delivered
                ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20"
                : "border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-950/20"
            }`}>
              <div className="flex items-center gap-2">
                {detail.fulfilment.delivered
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  : <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0" />}
                <span className="text-sm font-medium text-slate-900 dark:text-foreground">
                  {detail.fulfilment.delivered ? "Delivered" : "Not delivered"}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                  {humanize(detail.fulfilment.expected)} · {humanize(detail.fulfilment.status)}
                </span>
              </div>
              {detail.fulfilment.applied_at && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Applied {formatDate(detail.fulfilment.applied_at)}
                </p>
              )}
              {detail.fulfilment.error && (
                <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{detail.fulfilment.error}</p>
              )}
            </div>

            <Block title="Payment">
              <Field label="Amount">{money(detail.payment.amount, detail.payment.currency)}</Field>
              <Field label="Method">
                {methodLabel(detail.payment.payment_method) || (
                  <span className="text-slate-400 italic">not recorded</span>
                )}
              </Field>
              {/* The raw purpose is a code-level value (`company_signup`), only
                  useful for matching a row back to the call site that wrote it.
                  Subordinate to the label rather than a peer field — as its own
                  row it read like an unformatted string. */}
              <Field label="Type">
                {detail.payment.purpose_label}
                <span className="block text-[10px] font-mono text-slate-400 dark:text-slate-500 mt-0.5">
                  {detail.payment.purpose}
                </span>
              </Field>
              <Field label="Plan">{detail.payment.plan_id}</Field>
              {detail.payment.refund_processed && <Field label="Refunded">yes</Field>}
            </Block>

            {detail.company && (
              <Block title="Company">
                <Field label="Name">
                  <button
                    onClick={() => onOpenCompany(detail.company._id)}
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {detail.company.name} <ExternalLink className="h-3 w-3" />
                  </button>
                </Field>
                <Field label="Contact">{detail.company.contact_email}</Field>
                {detail.subscription && (
                  <Field label="Subscription">
                    {detail.subscription.plan_name} · {detail.subscription.status}
                  </Field>
                )}
              </Block>
            )}

            <Block title="Razorpay">
              <Field label="Payment id" mono>{detail.payment.razorpay_payment_id}</Field>
              <Field label="Order id" mono>{detail.payment.razorpay_order_id}</Field>
              <Field label="Subscription id" mono>{detail.payment.razorpay_subscription_id}</Field>
              {detail.payment.refund_id && <Field label="Refund id" mono>{detail.payment.refund_id}</Field>}
            </Block>

            {detail.payment.notes && Object.keys(detail.payment.notes).length > 0 && (
              <Block title="Notes">
                {Object.entries(detail.payment.notes).map(([k, v]) => (
                  <Field key={k} label={k}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</Field>
                ))}
              </Block>
            )}

            {/* The trace is why this drawer is worth opening — it shows what the
                system DID, not just what was stored. */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">
                Trace {detail.trace.trace_id && <span className="font-mono normal-case">· {detail.trace.trace_id.slice(0, 12)}</span>}
              </p>
              {!detail.trace.events.length ? (
                <p className="text-xs text-slate-500 dark:text-slate-400 px-3 py-2 rounded-lg border border-dashed border-slate-200 dark:border-slate-700">
                  No trace recorded — this payment predates tracing.
                </p>
              ) : (
                <ol className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                  {detail.trace.events.map((e, i) => (
                    <li key={i} className="px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className={`font-mono text-xs ${
                          e.level === "error" ? "text-rose-600 dark:text-rose-400"
                            : e.level === "warn" ? "text-amber-600 dark:text-amber-400"
                            : "text-slate-700 dark:text-slate-300"}`}>
                          {e.event}
                        </span>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {new Date(e.ts).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {e.entry}{e.duration_ms != null && ` · ${e.duration_ms}ms`}
                        {e.outcome && ` · ${e.outcome}`}
                      </div>
                      {(e.consequence || e.message) && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                          {e.consequence || e.message}
                        </p>
                      )}
                      {e.gateway?.description && (
                        <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{e.gateway.description}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </>
      )}
    </SheetContent>
  </Sheet>
);

/* ────────────────────────────────────────────────────────────────────────
   Health tab
   ──────────────────────────────────────────────────────────────────────── */

/**
 * `actionable` is the count of rows that genuinely need someone to act. It is
 * NOT cls.count — a section can be non-empty and still be fine (a provisionally
 * activated subscription, a mandate still inside its authorisation window).
 * Colouring by volume rather than severity is how a badge stops being read.
 */
const HealthSection = ({ title, description, cls, actionable = 0, children }) => (
  <Card className="rounded-2xl border border-slate-200/70 dark:border-slate-700">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-foreground flex items-center gap-2">
            {title}
            {cls?.count > 0 && (
              <span className={`inline-flex items-center justify-center rounded-full text-[11px] font-bold h-5 min-w-5 px-1.5 ${
                actionable > 0
                  ? "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
              }`}>
                {cls.count}
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>
        </div>
      </div>
      {!cls?.count ? (
        <p className="text-sm text-emerald-600 dark:text-emerald-400 py-2">Nothing to action.</p>
      ) : (
        <>
          {actionable === 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 pb-2 border-b border-slate-100 dark:border-slate-800">
              Visible for awareness — nothing here needs action right now.
            </p>
          )}
          {children}
          {cls.truncated && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Showing {cls.items.length} of {cls.count}.
            </p>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

const HealthTab = ({ health, loading, onOpenCompany }) => {
  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>;
  }
  if (!health) return null;

  const { unfulfilled_payments: unf, blocked_requests: blk, stale_mandates: mnd, trace_errors: err } = health.classes;

  const Row = ({ severity, children, companyId, consequence }) => (
    <div className="py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-sm">{children}</div>
        {companyId && (
          <Button variant="ghost" size="sm" className="shrink-0 h-7 text-xs"
            onClick={() => onOpenCompany(companyId)}>
            Open <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        )}
      </div>
      {consequence && (
        <p className={`text-xs mt-1 ${SEVERITY_TONE[severity] || "text-slate-500"}`}>{consequence}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <HealthSection
        title="Money captured, nothing delivered"
        description="Paid subscription charges where activation was never confirmed."
        cls={unf}
        actionable={unf.items.filter((i) => i.severity === "undelivered").length}
      >
        {unf.items.map((i) => (
          <Row key={i._id} severity={i.severity} companyId={i.company_id}
            consequence={i.severity === "undelivered"
              ? "No subscription was created for this payment."
              : "Provisionally activated — the confirming webhook never arrived. Service is live."}>
            <span className="font-medium">{money(i.amount, i.currency)}</span>
            <span className="text-slate-500 dark:text-slate-400"> · {i.purpose_label} · {formatDate(i.createdAt)}</span>
          </Row>
        ))}
      </HealthSection>

      <HealthSection
        title="Blocked purchase requests"
        description="Stuck rows that stop a company from starting any further purchase of that type."
        cls={blk}
        actionable={blk.count}
      >
        {blk.items.map((i) => (
          <Row key={i._id} severity={i.severity} companyId={i.company_id} consequence={i.consequence}>
            <span className="font-medium">{humanize(i.kind)}</span>
            <span className="text-slate-500 dark:text-slate-400">
              {" "}· {money(i.payable_now)} · stuck {i.age_hours}h
            </span>
          </Row>
        ))}
      </HealthSection>

      <HealthSection
        title="Mandates awaiting authorisation"
        description="UPI mandates staged but never authorised — these reject every seat and plan update."
        cls={mnd}
        actionable={health.totals.dead_mandates}
      >
        {mnd.items.map((i) => (
          <Row key={i._id} severity={i.severity} companyId={i.company_id} consequence={i.consequence}>
            <span className="font-medium">{i.plan_name || i.plan_id}</span>
            <span className="text-slate-500 dark:text-slate-400">
              {" "}· staged {i.age_days}d ago · {humanize(i.pending_mandate?.reason)}
            </span>
          </Row>
        ))}
      </HealthSection>

      <HealthSection
        title="Recent errors"
        description={`Billing failures recorded in the last ${health.params.hours}h.`}
        cls={err}
      >
        {err.items.map((e) => (
          <Row key={e.event} severity="blocking"
            consequence={e.sample?.consequence || e.sample?.message || null}>
            <span className="font-mono text-xs">{e.event}</span>
            <span className="text-slate-500 dark:text-slate-400">
              {" "}· {e.count}× across {e.company_count} compan{e.company_count === 1 ? "y" : "ies"}
              {e.raced && <span className="text-amber-600 dark:text-amber-400"> · raced</span>}
            </span>
          </Row>
        ))}
      </HealthSection>

      <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
        <Receipt className="h-3.5 w-3.5" />
        Computed from local records only. Open a company to check its live state against Razorpay.
      </p>
    </div>
  );
};

export default PlatformPayments;
